import React, { useState, useEffect } from 'react';
import { User } from '../../types';
import { travelService } from '../../services/travelService';
import { gameService } from '../../services/gameService';
import { useError } from '../../contexts/ErrorContext';

interface GTAProps {
  user: User;
  onUpdate: () => void;
}

interface StealAction {
  id: string;
  name: string;
  description: string;
  icon: string;
  baseSuccessRate: number;
  rankMultiplier: number;
  carRarityBonus: number;
  riskLevel: 'Low' | 'Medium' | 'High';
  color: string;
  cooldownMinutes: number;
  rankPoints: number;
  rankPointsRange: { min: number; max: number };
  image: string;
}

const GTA: React.FC<GTAProps> = ({ user, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [tooltipVisible, setTooltipVisible] = useState<{ [key: string]: boolean }>({});
  const { showError, showSuccess } = useError();

  // Update current time every second for countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const stealActions: StealAction[] = [
    {
      id: 'driveway',
      name: 'Steal from Driveway',
      description: 'Target cars parked in residential driveways. Lower risk, common cars.',
      icon: '🏠',
      baseSuccessRate: 75,
      rankMultiplier: 2,
      carRarityBonus: 5,
      riskLevel: 'Low',
      color: '#4caf50',
      cooldownMinutes: 3,
      rankPoints: 1,
      rankPointsRange: { min: 1, max: 2 },
      image: '/images/gta/driveway.jpg'
    },
    {
      id: 'street',
      name: 'Steal from Street',
      description: 'Target cars parked on the street. Medium risk, better variety.',
      icon: '🛣️',
      baseSuccessRate: 60,
      rankMultiplier: 3,
      carRarityBonus: 15,
      riskLevel: 'Medium',
      color: '#ff9800',
      cooldownMinutes: 3.17,
      rankPoints: 2,
      rankPointsRange: { min: 2, max: 4 },
      image: '/images/gta/street.jpg'
    },
    {
      id: 'mansion',
      name: 'Steal from Mansion',
      description: 'Target luxury cars at high-end properties. High risk, rare cars.',
      icon: '🏰',
      baseSuccessRate: 40,
      rankMultiplier: 4,
      carRarityBonus: 35,
      riskLevel: 'High',
      color: '#f44336',
      cooldownMinutes: 3.33,
      rankPoints: 3,
      rankPointsRange: { min: 3, max: 6 },
      image: '/images/gta/mansion.jpg'
    }
  ];

  // Get garage capacity based on garage ID (you may need to adjust this based on your backend)
  const getGarageCapacity = (garageId: number): number => {
    const garageCapacities: { [key: number]: number } = {
      1: 1,  // Basic garage
      2: 3,  // Medium garage
      3: 5,  // Large garage
      4: 10, // Premium garage
      5: 20  // Ultimate garage
    };
    return garageCapacities[garageId] || 1;
  };

  const isGarageFull = (): boolean => {
    const capacity = getGarageCapacity(user.garageId);
    return user.garageCars.length >= capacity;
  };

  const calculateSuccessRate = (action: StealAction): number => {
    const rankBonus = (user.rankId - 1) * action.rankMultiplier;
    const totalRate = action.baseSuccessRate + rankBonus;
    return Math.min(totalRate, 95); // Cap at 95%
  };

  const handleStealCar = async (actionId: string) => {
    setLoading(true);
    
    try {
      const result = await travelService.stealCar(actionId);
      
      if (result.success) {
        showSuccess(result.message);
        console.log('🚗 GTA Success: Car stolen! Refreshing user data...');
        await gameService.refreshUserStats();
        onUpdate();
        
        // Force a small delay to ensure data is updated
        setTimeout(() => {
          console.log('🔄 GTA: Final refresh to ensure cooldown is active...');
          onUpdate();
        }, 500);
      } else {
        showError(result.message);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error stealing car';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (riskLevel: string): string => {
    switch (riskLevel) {
      case 'Low': return '#4caf50';
      case 'Medium': return '#ff9800';
      case 'High': return '#f44336';
      default: return '#666';
    }
  };

  // Universal GTA cooldown - same as crimes system
  const getUniversalGTACooldown = (): { remainingMs: number; lastGTAName: string } | null => {
    if (!user.lastGTATime) return null;
    
    // Get cooldown time from last committed GTA
    let cooldownMinutes = 3; // Default fallback
    let lastGTAName = 'last GTA action';
    
    if (user.lastGTAType) {
      const lastGTAAction = stealActions.find(a => a.id === user.lastGTAType);
      if (lastGTAAction) {
        cooldownMinutes = lastGTAAction.cooldownMinutes;
        lastGTAName = lastGTAAction.name;
      }
    }
    
    const cooldownMs = cooldownMinutes * 60 * 1000;
    const timeSinceLastGTA = currentTime - new Date(user.lastGTATime).getTime();
    
    if (timeSinceLastGTA < cooldownMs) {
      return {
        remainingMs: cooldownMs - timeSinceLastGTA,
        lastGTAName: lastGTAName
      };
    }
    
    return null;
  };



  // Helper function to format time in human-readable format
  const formatTime = (minutes: number): string => {
    const mins = Math.floor(minutes);
    const secs = Math.round((minutes - mins) * 60);
    
    if (mins === 0) {
      return `${secs}s`;
    } else if (secs === 0) {
      return `${mins}m`;
    } else {
      return `${mins}m ${secs}s`;
    }
  };



  // Toggle tooltip visibility
  const toggleTooltip = (actionId: string) => {
    setTooltipVisible(prev => ({
      ...prev,
      [actionId]: !prev[actionId]
    }));
  };

  const formatCooldown = (ms: number): string => {
    const minutes = ms / (60 * 1000);
    return formatTime(minutes);
  };

  const universalCooldown = getUniversalGTACooldown();
  const isOnUniversalCooldown = universalCooldown !== null;

  // Debug logging - Check if cooldown system is working
  if (user.lastGTATime) {
    console.log('✅ GTA Cooldown System Working:', {
      lastGTATime: user.lastGTATime,
      lastGTAType: user.lastGTAType,
      isOnCooldown: isOnUniversalCooldown,
      remainingTime: universalCooldown?.remainingMs ? `${Math.round(universalCooldown.remainingMs / 1000)}s` : 'N/A'
    });
  } else {
    console.log('⏳ GTA: No cooldown active - ready to steal cars');
  }

  return (
    <div className="gta-page">
      <h2 className="page-title">Grand Theft Auto</h2>
      
      <div className="gta-content">

        {/* Universal GTA Cooldown Bar */}
        {isOnUniversalCooldown && (
          <div className="universal-cooldown-bar">
            <div className="cooldown-header">
              <span className="cooldown-icon">⏰</span>
              <span className="cooldown-title">Universal GTA Cooldown</span>
            </div>
            <div className="cooldown-progress">
              <div 
                className="cooldown-fill"
                style={{
                  width: `${((universalCooldown!.remainingMs / (3 * 60 * 1000)) * 100)}%`
                }}
              ></div>
            </div>
            <div className="cooldown-info">
              <span className="cooldown-time">{formatCooldown(universalCooldown!.remainingMs)} remaining</span>
              <span className="cooldown-source">from {universalCooldown!.lastGTAName}</span>
            </div>
          </div>
        )}

        <div className="gta-actions-container">
          {/* Garage Full Warning */}
          {isGarageFull() && (
            <div className="car-warning">
              ⚠️ Your garage is full ({user.garageCars.length}/{getGarageCapacity(user.garageId)} cars). Visit the Garage to sell vehicles.
            </div>
          )}

          {/* Stealing Actions */}
          <div className="steal-actions-grid">
            {stealActions.map((action) => {
              const successRate = calculateSuccessRate(action);
              const canSteal = !isGarageFull() && !isOnUniversalCooldown;
              
              return (
                <div 
                  key={action.id}
                  className={`steal-action-card ${!canSteal ? 'disabled' : ''}`}
                  onClick={() => canSteal && handleStealCar(action.id)}
                >
                  {/* Action Image */}
                  <div className="action-image-container">
                    <img 
                      src={action.image} 
                      alt={action.name}
                      className="action-image"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                    
                    {/* Risk Badge Overlay */}
                    <div className="risk-badge" style={{ backgroundColor: getRiskColor(action.riskLevel) }}>
                      {action.riskLevel} Risk
                    </div>
                  </div>

                  {/* Action Content */}
                  <div className="action-content">
                    <h4 className="action-name">
                      {action.name}
                    </h4>
                    
                    <div className="success-rate">
                      <span className="success-value" style={{ color: action.color }}>
                        {successRate}% Success Rate
                      </span>
                    </div>

                    {/* Button Container with Info Icon */}
                    <div className="button-container">
                      <button
                        className={`steal-button ${!canSteal ? 'disabled' : ''}`}
                        disabled={!canSteal || loading}
                        style={{
                          backgroundColor: action.color
                        }}
                      >
                        {loading ? 'Stealing...' : 
                         isOnUniversalCooldown ? 'On Cooldown' : 
                         isGarageFull() ? 'Garage Full' : 'Steal Car'}
                      </button>
                      
                      <div className="info-icon-container" style={{ position: 'relative' }}>
                        <div 
                          className="info-icon"
                          onClick={() => toggleTooltip(action.id)}
                        >
                          i
                        </div>
                        
                        {/* Info Tooltip */}
                        <div className={`info-tooltip ${tooltipVisible[action.id] ? 'visible' : ''}`}>
                          <div className="tooltip-title">{action.name} Details</div>
                          <div className="tooltip-item">
                            <span className="tooltip-label">Success Rate:</span>
                            <span className="tooltip-value" style={{ color: action.color }}>
                              {successRate}%
                            </span>
                          </div>
                          <div className="tooltip-item">
                            <span className="tooltip-label">Car Rarity Bonus:</span>
                            <span className="tooltip-value">+{action.carRarityBonus}%</span>
                          </div>
                          <div className="tooltip-item">
                            <span className="tooltip-label">Rank Points:</span>
                            <span className="tooltip-value">{action.rankPointsRange.min}-{action.rankPointsRange.max}</span>
                          </div>
                          <div className="tooltip-item">
                            <span className="tooltip-label">Cooldown:</span>
                            <span className="tooltip-value">{formatTime(action.cooldownMinutes)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GTA;
